<script setup lang="ts">
import { computed, ref } from 'vue'
import ConfirmDialog from 'primevue/confirmdialog'
import { useConfirm } from 'primevue/useconfirm'
import GbButton from '@/components/GbButton.vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { deleteGeneratedAssessment } from './api'
import { GENERATED_ASSESSMENT_EVENTS } from './event_names'
import type { DeleteOutcome } from './generated_assessment_types'

const props = defineProps<{
  assessmentUuid: string
  assessmentTitle: string
  schemaKey?: string | null
}>()

const emit = defineEmits<{
  deleted: [wasFocusedWhenClicked: boolean]
}>()

// Promises only what happens: the record leaves this goal and there is no in-app restore.
// The uploaded assets stay public-read, so the copy says nothing about them.
const CONFIRM_TITLE = 'Delete assessment?'
const CONFIRM_BODY = "It will be removed from this goal and can't be undone."

// One message for every failure, because the endpoint answers 404 for a revoked
// permission exactly as it does for an already-deleted record.
const FAILURE_TITLE = "Couldn't delete this assessment"
const FAILURE_BODY =
  'It may have already been deleted, or you may no longer have permission to delete it. ' +
  'Reload the page to see what is still there.'

const confirm = useConfirm()
const analytics = getAnalyticsTracker()
const buttonRef = ref<HTMLButtonElement | null>(null)
const isDeleting = ref(false)
const dialogGroup = computed(() => `delete-generated-assessment-${props.assessmentUuid}`)
const dialogTitleId = computed(() => `${dialogGroup.value}-title`)
// The container slot replaces PrimeVue's header, so the dialog is named by ours instead.
const dialogPassThrough = computed(() => ({ root: { 'aria-labelledby': dialogTitleId.value } }))

function trackDeletion(outcome: DeleteOutcome) {
  analytics.sendEvent(GENERATED_ASSESSMENT_EVENTS.DELETED, { outcome, schema_key: props.schemaKey ?? null })
}

function showFailure() {
  confirm.require({
    group: dialogGroup.value,
    header: FAILURE_TITLE,
    message: FAILURE_BODY,
    acceptLabel: 'OK'
  })
}

async function isDeletedOnServer(): Promise<boolean> {
  try {
    await deleteGeneratedAssessment(props.assessmentUuid)
    return true
  } catch (error) {
    console.error('Failed to delete generated assessment', error)
    return false
  }
}


// Reported only after the request settles: the event name is past tense, and firing on
// the click would count deletions the server refused.
async function deleteConfirmed(wasFocusedWhenClicked: boolean) {
  isDeleting.value = true
  const isDeleted = await isDeletedOnServer()
  isDeleting.value = false
  if (!isDeleted) {
    trackDeletion('failed')
    showFailure()
    return
  }
  trackDeletion('success')
  emit('deleted', wasFocusedWhenClicked)
}

function confirmDelete() {
  // Read now: once the dialog has opened and closed, focus no longer says whether this
  // was a keyboard interaction that needs focus put back after the tile is removed.
  const wasFocusedWhenClicked = document.activeElement === buttonRef.value
  // PrimeVue keeps the dialog's Delete button live while it animates out, so a double
  // click or held Enter would otherwise accept twice and send a second DELETE.
  let isAccepted = false
  const acceptOnce = () => {
    if (isAccepted) {
      return
    }
    isAccepted = true
    deleteConfirmed(wasFocusedWhenClicked)
  }
  confirm.require({
    group: dialogGroup.value,
    header: CONFIRM_TITLE,
    message: CONFIRM_BODY,
    acceptLabel: 'Delete',
    rejectLabel: 'Cancel',
    accept: acceptOnce
  })
}
</script>

<template>
  <button
    ref="buttonRef"
    type="button"
    data-testid="delete-generated-assessment"
    class="inline-flex items-center justify-center rounded-xs px-1 py-0.5 text-red-200 hover:bg-red-200 hover:text-white focus:bg-red-200 focus:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400 disabled:cursor-not-allowed disabled:opacity-60"
    :aria-label="`Delete ${props.assessmentTitle}`"
    :disabled="isDeleting"
    @click="confirmDelete"
  >
    <span class="material-icons-round text-text-2xl" aria-hidden="true">close</span>
  </button>
  <ConfirmDialog :group="dialogGroup" :pt="dialogPassThrough" class="w-auto md:w-[38rem]">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="flex flex-col rounded-md bg-surface-50" data-testid="delete-generated-assessment-dialog">
        <div class="mt-2 border-b border-gray-300 p-3">
          <span :id="dialogTitleId" class="block pl-3 text-xl font-medium">{{ message.header }}</span>
        </div>
        <p class="py-5 pl-7 pr-12">{{ message.message }}</p>
        <div class="flex items-center justify-end gap-2 border-t border-gray-300 p-4">
          <GbButton
            v-if="message.rejectLabel"
            :label="message.rejectLabel"
            gbTheme="grayOutlineSecondary"
            @click="rejectCallback"
          />
          <!-- The container slot skips PrimeVue's own focus target, so focus starts on Delete, as Vue 2's dialog did -->
          <GbButton :label="message.acceptLabel" autofocus gbTheme="bluePrimary" @click="acceptCallback" />
        </div>
      </div>
    </template>
  </ConfirmDialog>
</template>
